/**
 * Admin -> Edge Fleet -> Server scanner load policy (release 3.1 fleet wave 2a,
 * review fixes 2 and 3).
 *
 * A server scanner reads customer production databases. How hard it may lean
 * on them used to be set only on the host that ran it (environment
 * variables), so the host could switch the load governor off, and its database
 * sessions had no statement timeout. The operator now sets ONE policy for every
 * server scanner of the tenant, sent on each of their heartbeats and applied
 * over anything their environment says:
 *
 *   - the load governor: on or off, the readings it takes before acting, the
 *     latency (as a multiple of the calm baseline) at which it slows a target
 *     and at which it pauses one, and the breaches before a target is left
 *     for the cycle;
 *   - the per-statement timeout on every target session;
 *   - how SQL Server reads treat locks (engine default, snapshot or no-lock),
 *     so a scan does not block the customer's workload (release 3.6 #28);
 *   - queries per second across all of one scanner's targets;
 *   - targets one scanner scans at once;
 *   - a lower ceiling on concurrent reads for named systems (host:port), so one
 *     fragile database can be protected without slowing every other one
 *     (release 3.6 #27).
 *
 * Every change needs a reason (audited `edge.scanner.policy_updated`); turning
 * the governor off asks for a second click, because that is the change that
 * lets a scan hit a slow production database at full speed.
 *
 * Backend:
 *   GET /v1/edge/agent-types/server_scanner/settings -> { settings, agentPolicy }
 *   PUT /v1/edge/agent-types/server_scanner/settings  { governor, statementTimeoutMs, readIsolation, maxQueriesPerSecond, maxConcurrentTargets, systemLimits, reason }
 * A controller without it answers 404: the panel says so.
 */
import { useCallback, useEffect, useState } from 'react';
import { Gauge, Loader2, Pencil, Plus, RefreshCw, Save, X } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ApiError,
  getScannerPolicy,
  problemDetail,
  SCANNER_POLICY_LIMITS,
  updateScannerPolicy,
  type ScannerPolicyResponse,
  type ScannerReadIsolation,
  type ScannerSystemLimit,
  type ScannerSystemLimitSharing,
} from '@/lib/api';

export interface ScannerPolicyForm {
  enabled: boolean;
  warmUp: string;
  slowFactor: string;
  pauseFactor: string;
  stopAfter: string;
  /** Seconds in the form; the API takes milliseconds. */
  statementTimeoutSeconds: string;
  readIsolation: ScannerReadIsolation;
  maxQueriesPerSecond: string;
  maxConcurrentTargets: string;
  systemLimits: SystemLimitFormRow[];
  reason: string;
}

/**
 * One system's row in the editor. The release 3.6 T-27 fields are strings
 * like the rest of the form; empty means not set. Every field the controller
 * stored is carried back on save, so saving the form never drops a system's
 * load rule, window or pause.
 */
export interface SystemLimitFormRow {
  system: string;
  maxConcurrentReads: string;
  pauseAtCpuPercent?: string;
  resumeAtCpuPercent?: string;
  pauseAtActiveSessions?: string;
  resumeAtActiveSessions?: string;
  sampleEverySeconds?: string;
  maxPauseMinutes?: string;
  window?: string;
  windowUtcOffsetMinutes?: string;
  paused?: boolean;
  maxConnections?: string;
}

/** A stored limit as an editor row. */
export function limitToFormRow(l: ScannerSystemLimit): SystemLimitFormRow {
  const r = l.sourceLoad;
  const opt = (v: number | undefined): string => (v === undefined ? '' : String(v));
  return {
    system: l.system,
    maxConcurrentReads: String(l.maxConcurrentReads),
    pauseAtCpuPercent: opt(r?.pauseAtCpuPercent),
    resumeAtCpuPercent: opt(r?.resumeAtCpuPercent),
    pauseAtActiveSessions: opt(r?.pauseAtActiveSessions),
    resumeAtActiveSessions: opt(r?.resumeAtActiveSessions),
    sampleEverySeconds: r ? String(r.sampleEverySeconds) : '',
    maxPauseMinutes: r ? String(r.maxPauseMinutes) : '',
    window: l.window ?? '',
    windowUtcOffsetMinutes: opt(l.windowUtcOffsetMinutes),
    paused: l.paused === true,
    maxConnections: opt(l.maxConnections),
  };
}

/** What a limit's T-27 fields ask, in words (the same wording as the device row). */
export function sourceControlsSummary(l: ScannerSystemLimit): string | null {
  const parts: string[] = [];
  const r = l.sourceLoad;
  if (r) {
    if (r.pauseAtCpuPercent !== undefined) parts.push(`pause at CPU ${r.pauseAtCpuPercent}%`);
    if (r.pauseAtActiveSessions !== undefined) parts.push(`pause at ${r.pauseAtActiveSessions.toLocaleString('en-IN')} active sessions`);
  }
  if (l.window) parts.push(`window ${l.window}`);
  if (l.paused) parts.push('PAUSED');
  if (l.maxConnections !== undefined) parts.push(`at most ${l.maxConnections} connections`);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** The sentence under the source-load fields (panel and runbook). */
export const SOURCE_LOAD_HELP =
  'The scanner reads the source server itself: active sessions on every engine, CPU on SQL Server and Oracle only ' +
  '(a CPU level on PostgreSQL or MySQL never pauses). Reads pause at a pause level and resume at or below the resume level ' +
  '(80% of the pause level when empty). The scan account needs pg_monitor (PostgreSQL), VIEW SERVER STATE (SQL Server) or ' +
  'SELECT on V_$SESSION and V_$SYSMETRIC (Oracle). Paused, or outside its window, a system is not read and the scan reports it as partial. ' +
  'A pause saved during a scan applies to that scan only for a system it was already watching (one with a load rule, window or pause when it started); ' +
  'for any other system it applies from the next scan, and the device row shows pending until then. ' +
  'Per scanner: nothing coordinates two scanners reading one system.';

type Input = Parameters<typeof updateScannerPolicy>[0];

const ISOLATION_OPTIONS: Record<ScannerReadIsolation, { label: string; help: string }> = {
  engine_default: { label: 'Engine default', help: 'SQL Server reads take shared locks and can wait behind a writer.' },
  snapshot: {
    label: 'Snapshot isolation',
    help: 'SQL Server reads a consistent snapshot and takes no shared locks. Needs ALLOW_SNAPSHOT_ISOLATION on the database (row versions use tempdb); where it is off the scanner keeps the default and logs why.',
  },
  no_lock: {
    label: 'No-lock (read uncommitted)',
    help: 'SQL Server reads take no locks and never wait. A sample can include uncommitted rows (a finding can come from a row that is later rolled back), skip or repeat rows, or fail with error 601.',
  },
};

// The controller's own pattern for a system (edge/scanner-policy.ts
// isSystemKey), so the form never sends one it refuses: host:port, or an IPv6
// address in brackets ([fd00::5]:5432).
const SYSTEM_PATTERN = /^(?:[a-z0-9](?:[a-z0-9._-]{0,251}[a-z0-9])?|\[[0-9a-f:.]{2,45}\]):[0-9]{1,5}$/;

function isIpv6(literal: string): boolean {
  try {
    return new URL(`http://[${literal}]/`).hostname.startsWith('[');
  } catch {
    return false;
  }
}

/**
 * A per-system limit is PER SCANNER (PR #191 round 3): every server scanner of
 * the tenant holds the same list and enforces it in its own process, with no
 * lock across scanners. The same sentence as the controller's row note
 * (limits-comparison.ts SYSTEM_LIMIT_PER_SCANNER) and the runbook.
 */
export const SYSTEM_LIMIT_PER_SCANNER =
  'Per scanner: each server scanner reading this system applies the limit on its own, ' +
  'so N scanners reading it can hold up to N times this many reads.';

/**
 * One warning per limit that more than one scanner applied in its latest scan:
 * "applied by 2 scanners: the database can see up to 2 x 1 reads". Bounded by
 * the policy's own list (at most 200); a count for a system the policy does
 * not hold is ignored.
 */
export function sharedLimitWarnings(
  limits: ReadonlyArray<{ system: string; maxConcurrentReads: number }>,
  sharing: ScannerSystemLimitSharing | undefined,
): Array<{ system: string; text: string }> {
  if (!sharing || !Array.isArray(sharing.systems)) return [];
  const scope = sharing.scope === 'business_unit' ? ' (counted over the scanners of your business units only)' : '';
  return limits.slice(0, SCANNER_POLICY_LIMITS.systemLimits.max).flatMap((l) => {
    const k = sharing.systems.find((x) => x.system === l.system)?.scanners ?? 0;
    if (!Number.isInteger(k) || k <= 1) return [];
    return [
      {
        system: l.system,
        text:
          `${l.system}: applied by ${k} scanners${scope}: the database can see up to ${k} x ${l.maxConcurrentReads} reads. ` +
          'To hold it to the limit, let one scanner read it, or stagger their scan hours.',
      },
    ];
  });
}

export function isSystemKey(system: string): boolean {
  if (!SYSTEM_PATTERN.test(system)) return false;
  const cut = system.lastIndexOf(':');
  if (Number(system.slice(cut + 1)) > 65_535) return false;
  const host = system.slice(0, cut);
  return host.startsWith('[') ? isIpv6(host.slice(1, -1)) : true;
}

const isolationLabel = (v: ScannerReadIsolation) => ISOLATION_OPTIONS[v]?.label ?? v;

function whole(v: string, min: number, max: number): number | null {
  const n = Number(v);
  return v.trim() && Number.isInteger(n) && n >= min && n <= max ? n : null;
}

function real(v: string, min: number, max: number): number | null {
  const n = Number(v);
  return v.trim() && Number.isFinite(n) && n >= min && n <= max ? n : null;
}

/**
 * The body for the PUT, or the first thing wrong with the form. Pure, so the
 * bounds are tested without a browser; they are the controller's own
 * (SCANNER_POLICY_LIMITS), so the form never offers a value the server refuses.
 */
export function scannerPolicyFormToInput(f: ScannerPolicyForm): { ok: true; input: Input } | { ok: false; error: string } {
  const L = SCANNER_POLICY_LIMITS;
  const warmUp = whole(f.warmUp, L.warmUp.min, L.warmUp.max);
  if (warmUp === null) return { ok: false, error: `Readings before acting must be a whole number from ${L.warmUp.min} to ${L.warmUp.max}.` };
  const slow = real(f.slowFactor, L.slowFactor.min, L.slowFactor.max);
  if (slow === null) return { ok: false, error: `Slow down at must be from ${L.slowFactor.min} to ${L.slowFactor.max} times the baseline.` };
  const pause = real(f.pauseFactor, slow + Number.EPSILON, L.pauseFactor.max);
  if (pause === null || pause <= slow) {
    return { ok: false, error: `Pause at must be above slow down at (${slow}) and at most ${L.pauseFactor.max} times the baseline.` };
  }
  const stopAfter = whole(f.stopAfter, L.stopAfter.min, L.stopAfter.max);
  if (stopAfter === null) return { ok: false, error: `Breaches before stopping must be a whole number from ${L.stopAfter.min} to ${L.stopAfter.max}.` };
  const seconds = Number(f.statementTimeoutSeconds);
  const ms = Math.round(seconds * 1000);
  if (!f.statementTimeoutSeconds.trim() || !Number.isFinite(seconds) || ms < L.statementTimeoutMs.min || ms > L.statementTimeoutMs.max) {
    return { ok: false, error: `Statement timeout must be from ${L.statementTimeoutMs.min / 1000} to ${L.statementTimeoutMs.max / 1000} seconds.` };
  }
  const qps = real(f.maxQueriesPerSecond, L.maxQueriesPerSecond.min, L.maxQueriesPerSecond.max);
  if (qps === null) {
    return { ok: false, error: `Queries a second must be from ${L.maxQueriesPerSecond.min} to ${L.maxQueriesPerSecond.max.toLocaleString('en-IN')}.` };
  }
  const targets = whole(f.maxConcurrentTargets, L.maxConcurrentTargets.min, L.maxConcurrentTargets.max);
  if (targets === null) {
    return { ok: false, error: `Targets at once must be a whole number from ${L.maxConcurrentTargets.min} to ${L.maxConcurrentTargets.max}.` };
  }
  const systemLimits: NonNullable<Input['systemLimits']> = [];
  for (const row of f.systemLimits) {
    const system = row.system.trim().toLowerCase();
    if (!isSystemKey(system)) {
      return {
        ok: false,
        error: `System "${row.system.trim()}" must be host:port (an IPv6 address in brackets), for example db1.example.in:5432 or [fd00::5]:5432.`,
      };
    }
    const reads = whole(row.maxConcurrentReads, L.maxConcurrentReads.min, L.maxConcurrentReads.max);
    if (reads === null) {
      return { ok: false, error: `Concurrent reads for ${system} must be a whole number from ${L.maxConcurrentReads.min} to ${L.maxConcurrentReads.max}.` };
    }
    if (systemLimits.some((l) => l.system === system)) return { ok: false, error: `${system} is listed twice.` };
    const extras = sourceControlsOf(row, system);
    if (!extras.ok) return extras;
    systemLimits.push({ system, maxConcurrentReads: reads, ...extras.value });
  }
  if (systemLimits.length > L.systemLimits.max) {
    return { ok: false, error: `At most ${L.systemLimits.max} systems can have their own limit.` };
  }
  const reason = f.reason.trim();
  if (reason.length < L.reason.min) {
    return { ok: false, error: `Give a reason of at least ${L.reason.min} characters; it is recorded in the audit log.` };
  }
  return {
    ok: true,
    input: {
      governor: { enabled: f.enabled, warmUp, slowFactor: slow, pauseFactor: pause, stopAfter },
      statementTimeoutMs: ms,
      readIsolation: f.readIsolation,
      maxQueriesPerSecond: qps,
      maxConcurrentTargets: targets,
      systemLimits,
      reason: reason.slice(0, L.reason.max),
    },
  };
}

/** The T-27 fields of an editor row, checked against the controller's ranges. */
function sourceControlsOf(
  row: SystemLimitFormRow,
  system: string,
): { ok: true; value: Partial<ScannerSystemLimit> } | { ok: false; error: string } {
  const L = SCANNER_POLICY_LIMITS;
  const out: Partial<ScannerSystemLimit> = {};
  const blank = (v: string | undefined): boolean => (v ?? '').trim() === '';
  const pCpu = blank(row.pauseAtCpuPercent) ? undefined : real(row.pauseAtCpuPercent ?? '', L.cpuPercent.min, L.cpuPercent.max);
  if (pCpu === null) return { ok: false, error: `Pause at CPU for ${system} must be from ${L.cpuPercent.min} to ${L.cpuPercent.max} %.` };
  const rCpu = blank(row.resumeAtCpuPercent) ? undefined : real(row.resumeAtCpuPercent ?? '', 0, L.cpuPercent.max);
  if (rCpu === null || (rCpu !== undefined && (pCpu === undefined || rCpu >= pCpu))) {
    return { ok: false, error: `Resume at CPU for ${system} must be below its pause level.` };
  }
  const pS = blank(row.pauseAtActiveSessions) ? undefined : whole(row.pauseAtActiveSessions ?? '', L.activeSessions.min, L.activeSessions.max);
  if (pS === null) return { ok: false, error: `Pause at active sessions for ${system} must be a whole number from ${L.activeSessions.min} to ${L.activeSessions.max.toLocaleString('en-IN')}.` };
  const rS = blank(row.resumeAtActiveSessions) ? undefined : whole(row.resumeAtActiveSessions ?? '', 0, L.activeSessions.max);
  if (rS === null || (rS !== undefined && (pS === undefined || rS >= pS))) {
    return { ok: false, error: `Resume at active sessions for ${system} must be below its pause level.` };
  }
  if (pCpu !== undefined || pS !== undefined) {
    const every = blank(row.sampleEverySeconds) ? 30 : whole(row.sampleEverySeconds ?? '', L.sampleEverySeconds.min, L.sampleEverySeconds.max);
    if (every === null) return { ok: false, error: `Read the load every for ${system} must be from ${L.sampleEverySeconds.min} to ${L.sampleEverySeconds.max} seconds.` };
    const maxPause = blank(row.maxPauseMinutes) ? 30 : whole(row.maxPauseMinutes ?? '', L.maxPauseMinutes.min, L.maxPauseMinutes.max);
    if (maxPause === null) return { ok: false, error: `Longest pause for ${system} must be from ${L.maxPauseMinutes.min} to ${L.maxPauseMinutes.max} minutes.` };
    out.sourceLoad = {
      ...(pCpu !== undefined ? { pauseAtCpuPercent: pCpu } : {}),
      ...(rCpu !== undefined ? { resumeAtCpuPercent: rCpu } : {}),
      ...(pS !== undefined ? { pauseAtActiveSessions: pS } : {}),
      ...(rS !== undefined ? { resumeAtActiveSessions: rS } : {}),
      sampleEverySeconds: every,
      maxPauseMinutes: maxPause,
    };
  } else if (!blank(row.sampleEverySeconds) || !blank(row.maxPauseMinutes)) {
    return { ok: false, error: `${system}: set a pause level (CPU or active sessions) to use the load fields.` };
  }
  const w = (row.window ?? '').trim();
  if (w) {
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d-(?:[01]\d|2[0-3]):[0-5]\d$/.test(w) || w.slice(0, 5) === w.slice(6)) {
      return { ok: false, error: `Window for ${system} must be HH:MM-HH:MM (24-hour) with a different start and end, for example 01:00-05:00.` };
    }
    out.window = w;
    if (!blank(row.windowUtcOffsetMinutes)) {
      const o = Number((row.windowUtcOffsetMinutes ?? '').trim());
      if (!Number.isInteger(o) || o < L.windowUtcOffsetMinutes.min || o > L.windowUtcOffsetMinutes.max) {
        return { ok: false, error: `The window offset for ${system} must be whole minutes from ${L.windowUtcOffsetMinutes.min} to ${L.windowUtcOffsetMinutes.max} (330 is IST).` };
      }
      out.windowUtcOffsetMinutes = o;
    }
  } else if (!blank(row.windowUtcOffsetMinutes)) {
    return { ok: false, error: `${system}: the window offset needs a window.` };
  }
  if (row.paused) out.paused = true;
  if (!blank(row.maxConnections)) {
    const c = whole(row.maxConnections ?? '', L.maxConnections.min, L.maxConnections.max);
    if (c === null) return { ok: false, error: `Connections for ${system} must be a whole number from ${L.maxConnections.min} to ${L.maxConnections.max}.` };
    out.maxConnections = c;
  }
  return { ok: true, value: out };
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '-';
}

function seconds(ms: number): string {
  const s = ms / 1000;
  return `${Number.isInteger(s) ? s : s.toFixed(1)} s`;
}

const INPUT_CLASS = 'rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm text-foreground';

export function ScannerPolicyPanel() {
  const [data, setData] = useState<ScannerPolicyResponse | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ScannerPolicyForm | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getScannerPolicy());
      setUnavailable(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setUnavailable(true);
      else setError(problemDetail(e, 'could not load the server scanner policy'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const startEdit = () => {
    if (!data) return;
    const s = data.settings;
    setForm({
      enabled: s.governor.enabled,
      warmUp: String(s.governor.warmUp),
      slowFactor: String(s.governor.slowFactor),
      pauseFactor: String(s.governor.pauseFactor),
      stopAfter: String(s.governor.stopAfter),
      statementTimeoutSeconds: String(s.statementTimeoutMs / 1000),
      // A stored value the install no longer offers would be refused on save.
      readIsolation: s.readIsolationAvailable ? s.readIsolation : 'engine_default',
      maxQueriesPerSecond: String(s.maxQueriesPerSecond),
      maxConcurrentTargets: String(s.maxConcurrentTargets),
      systemLimits: (s.systemLimits ?? []).map(limitToFormRow),
      reason: '',
    });
    setConfirmOff(false);
    setFormError(null);
    setNotice(null);
    setEditing(true);
  };

  const turningOff = !!data && !!form && data.settings.governor.enabled && !form.enabled;
  // Absent from a controller before release 3.6 #27 (it predates the list).
  const limitsSupported = Array.isArray(data?.settings.systemLimits);

  const save = async () => {
    if (!form) return;
    const parsed = scannerPolicyFormToInput(form);
    if (!parsed.ok) {
      setFormError(parsed.error);
      return;
    }
    if (turningOff && !confirmOff) {
      setFormError('Confirm that the governor may be turned off (the box below) before saving.');
      return;
    }
    setBusy(true);
    setFormError(null);
    setNotice(null);
    // A controller before 3.6 #27 sends no list and refuses one in the body.
    const { systemLimits, ...rest } = parsed.input;
    try {
      setData(await updateScannerPolicy(limitsSupported ? { ...rest, systemLimits } : rest));
      setEditing(false);
      setNotice('Saved. Each server scanner applies it on its next check-in, over anything its host sets.');
    } catch (e) {
      setFormError(problemDetail(e, 'the change was refused'));
    } finally {
      setBusy(false);
    }
  };

  const s = data?.settings;
  const set = <K extends keyof ScannerPolicyForm>(k: K, v: ScannerPolicyForm[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const numberField = (id: string, label: string, key: keyof ScannerPolicyForm, width = 'w-28') => (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs text-muted-foreground">
        {label}
      </label>
      <input
        id={id}
        type="number"
        step="any"
        className={`${width} ${INPUT_CLASS}`}
        value={String(form?.[key] ?? '')}
        onChange={(e) => set(key, e.target.value as never)}
      />
    </div>
  );

  return (
    <GlassCard className="mt-4 p-0" data-testid="scanner-policy-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="h-4 w-4" />
            Server scanner load policy
          </CardTitle>
          <CardDescription>
            How hard every server scanner may lean on the databases it reads. Each scanner receives this on
            its check-ins and applies it over its own settings, so a host cannot switch the load governor off.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="Reload the server scanner policy">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>

      <div className="space-y-4 px-6 pb-6">
        {loading && !data && !unavailable && !error ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </p>
        ) : null}
        {unavailable ? (
          <p className="text-sm text-muted-foreground" data-testid="scanner-policy-unavailable">
            This controller does not offer a server scanner policy (it predates release 3.1).
          </p>
        ) : null}
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        {s ? (
          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground">Load governor</span>
                <Badge variant={s.governor.enabled ? 'success' : 'warning'} data-testid="scanner-policy-governor">
                  {s.governor.enabled ? 'on' : 'off'}
                </Badge>
              </div>
              {!editing ? (
                <Button size="sm" variant="ghost" onClick={startEdit}>
                  <Pencil className="mr-1 h-3 w-3" /> Edit policy
                </Button>
              ) : null}
            </div>
            <dl className="grid grid-cols-2 gap-3 text-xs md:grid-cols-5" data-testid="scanner-policy-values">
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">Slow down / pause at</dt>
                <dd className="text-sm tabular-nums text-foreground">
                  {s.governor.slowFactor}x / {s.governor.pauseFactor}x baseline
                </dd>
              </div>
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">Readings before acting / breaches before stopping</dt>
                <dd className="text-sm tabular-nums text-foreground">
                  {s.governor.warmUp} / {s.governor.stopAfter}
                </dd>
              </div>
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">Statement timeout</dt>
                <dd className="text-sm tabular-nums text-foreground">{seconds(s.statementTimeoutMs)}</dd>
              </div>
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">SQL Server read locks</dt>
                <dd className="text-sm text-foreground" data-testid="scanner-policy-isolation">
                  {isolationLabel(s.readIsolation)}
                </dd>
              </div>
              <div className="glass-lite rounded p-3">
                <dt className="mb-0.5 text-muted-foreground">Queries a second / targets at once</dt>
                <dd className="text-sm tabular-nums text-foreground">
                  {s.maxQueriesPerSecond} / {s.maxConcurrentTargets}
                </dd>
              </div>
            </dl>
            <div className="text-xs" data-testid="scanner-policy-system-limits">
              <span className="text-muted-foreground">Concurrent reads per system: </span>
              <span className="text-foreground">
                {!limitsSupported
                  ? 'not available on this controller (it predates per-system limits)'
                  : (s.systemLimits ?? []).length === 0
                    ? 'none set (every system follows the targets-at-once limit)'
                    : (s.systemLimits ?? []).map((l) => `${l.system} = ${l.maxConcurrentReads}`).join(', ')}
              </span>
              {limitsSupported && (s.systemLimits ?? []).length > 0 && (
                <p className="text-muted-foreground" data-testid="scanner-policy-system-limits-per-scanner">
                  {SYSTEM_LIMIT_PER_SCANNER}
                </p>
              )}
              {(s.systemLimits ?? []).flatMap((l) => {
                const text = sourceControlsSummary(l);
                return text
                  ? [
                      <p key={`src-${l.system}`} className={l.paused ? 'text-warning' : 'text-foreground/90'} data-testid={`scanner-policy-source-controls-${l.system}`}>
                        {l.system}: {text}
                      </p>,
                    ]
                  : [];
              })}
              {sharedLimitWarnings(s.systemLimits ?? [], data?.systemLimitSharing).map((w) => (
                <p key={w.system} className="text-warning" data-testid={`scanner-policy-system-shared-${w.system}`}>
                  {w.text}
                </p>
              ))}
              {(s.systemLimitsUnreadable ?? 0) > 0 && (
                <p className="text-warning" data-testid="scanner-policy-system-limits-unreadable">
                  {s.systemLimitsUnreadable} stored{' '}
                  {s.systemLimitsUnreadable === 1 ? 'entry could not be read and is' : 'entries could not be read and are'} not
                  sent to any scanner. Saving the policy keeps only the limits listed above.
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="scanner-policy-source">
              {s.isDefault
                ? 'The defaults: nobody has changed them. The governor is on by default.'
                : `Changed ${when(s.updatedAt)}${s.updatedBy ? ` by ${s.updatedBy}` : ''}${s.reason ? `: ${s.reason}` : ''}`}
            </p>

            {editing && form ? (
              <div className="space-y-2 rounded-md border border-border/40 bg-muted/20 p-3">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => {
                      set('enabled', e.target.checked);
                      setConfirmOff(false);
                    }}
                  />
                  Load governor on
                </label>
                <div className="flex flex-wrap items-end gap-3">
                  {numberField('sp-warmup', `Readings before acting (${SCANNER_POLICY_LIMITS.warmUp.min}-${SCANNER_POLICY_LIMITS.warmUp.max})`, 'warmUp')}
                  {numberField('sp-slow', 'Slow down at (x baseline)', 'slowFactor')}
                  {numberField('sp-pause', 'Pause at (x baseline)', 'pauseFactor')}
                  {numberField('sp-stop', 'Breaches before stopping', 'stopAfter')}
                  {numberField('sp-timeout', 'Statement timeout, seconds', 'statementTimeoutSeconds', 'w-32')}
                  {numberField('sp-qps', 'Queries a second', 'maxQueriesPerSecond', 'w-32')}
                  {numberField('sp-targets', 'Targets at once', 'maxConcurrentTargets')}
                  <div>
                    <label htmlFor="sp-isolation" className="mb-1 block text-xs text-muted-foreground">
                      SQL Server read locks
                    </label>
                    <select
                      id="sp-isolation"
                      className={INPUT_CLASS}
                      value={form.readIsolation}
                      onChange={(e) => set('readIsolation', e.target.value as ScannerReadIsolation)}
                    >
                      {(Object.keys(ISOLATION_OPTIONS) as ScannerReadIsolation[])
                        .filter((k) => s.readIsolationAvailable || k === 'engine_default')
                        .map((k) => (
                          <option key={k} value={k}>
                            {ISOLATION_OPTIONS[k].label}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="min-w-48 flex-1">
                    <label htmlFor="sp-reason" className="mb-1 block text-xs text-muted-foreground">
                      Reason for the change (recorded in the audit log)
                    </label>
                    <input
                      id="sp-reason"
                      className={`w-full ${INPUT_CLASS}`}
                      value={form.reason}
                      maxLength={SCANNER_POLICY_LIMITS.reason.max}
                      onChange={(e) => set('reason', e.target.value)}
                    />
                  </div>
                </div>
                {limitsSupported && (
                <fieldset className="space-y-1" data-testid="scanner-policy-system-rows">
                  <legend className="text-xs text-muted-foreground" data-testid="scanner-policy-system-legend">
                    Concurrent reads per system. {SYSTEM_LIMIT_PER_SCANNER} It lowers Targets at once for one database
                    (host:port as in its connection string; an IPv6 address in brackets); it never raises it. A host
                    name and its IP address are different systems. Only PostgreSQL, MySQL, SQL Server and Oracle targets
                    are gated: MongoDB, DB2 for i, warehouses, files and every other target never are. Oracle TNS
                    aliases, sockets and SQL Server named instances name no single address and cannot be listed (give a
                    named instance&apos;s TCP port). A limit no target of a scan matched shows as not matched under the
                    scanner&apos;s limits.
                  </legend>
                  {form.systemLimits.map((row, i) => (
                    <div key={i} className="flex items-end gap-2">
                      <input
                        aria-label={`System ${i + 1}, host:port`}
                        placeholder="db1.example.in:5432"
                        className={`w-64 ${INPUT_CLASS}`}
                        value={row.system}
                        onChange={(e) =>
                          set('systemLimits', form.systemLimits.map((r, j) => (j === i ? { ...r, system: e.target.value } : r)))
                        }
                      />
                      <input
                        aria-label={`System ${i + 1}, concurrent reads`}
                        type="number"
                        className={`w-24 ${INPUT_CLASS}`}
                        value={row.maxConcurrentReads}
                        onChange={(e) =>
                          set('systemLimits', form.systemLimits.map((r, j) => (j === i ? { ...r, maxConcurrentReads: e.target.value } : r)))
                        }
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Remove system ${i + 1}`}
                        onClick={() => set('systemLimits', form.systemLimits.filter((_, j) => j !== i))}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                      <label className="flex items-center gap-1 text-xs text-foreground">
                        <input
                          type="checkbox"
                          aria-label={`System ${i + 1}, paused`}
                          checked={row.paused === true}
                          onChange={(e) =>
                            set('systemLimits', form.systemLimits.map((r, j) => (j === i ? { ...r, paused: e.target.checked } : r)))
                          }
                        />
                        Paused
                      </label>
                    </div>
                  )).flatMap((line, i) => {
                    const row = form.systemLimits[i]!;
                    const field = (key: keyof SystemLimitFormRow, label: string, placeholder: string, width = 'w-20') => (
                      <input
                        key={key}
                        aria-label={`System ${i + 1}, ${label}`}
                        title={label}
                        placeholder={placeholder}
                        className={`${width} ${INPUT_CLASS}`}
                        value={String(row[key] ?? '')}
                        onChange={(e) =>
                          set('systemLimits', form.systemLimits.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)))
                        }
                      />
                    );
                    return [
                      line,
                      <div key={`src-${i}`} className="flex flex-wrap items-end gap-2 pl-2 text-xs" data-testid={`scanner-policy-source-fields-${i}`}>
                        {field('pauseAtCpuPercent', 'pause at CPU %', 'CPU %')}
                        {field('resumeAtCpuPercent', 'resume at CPU %', 'resume %')}
                        {field('pauseAtActiveSessions', 'pause at active sessions', 'sessions', 'w-24')}
                        {field('resumeAtActiveSessions', 'resume at active sessions', 'resume', 'w-20')}
                        {field('sampleEverySeconds', 'read the load every (seconds)', '30 s')}
                        {field('maxPauseMinutes', 'longest pause (minutes)', '30 min')}
                        {field('window', 'window HH:MM-HH:MM', '01:00-05:00', 'w-28')}
                        {field('windowUtcOffsetMinutes', 'window UTC offset (minutes, 330 is IST)', '330')}
                        {field('maxConnections', 'at most this many connections', 'conns')}
                      </div>,
                    ];
                  })}
                  <p className="text-xs text-muted-foreground" data-testid="scanner-policy-source-load-help">
                    {SOURCE_LOAD_HELP}
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => set('systemLimits', [...form.systemLimits, { system: '', maxConcurrentReads: '1' }])}
                  >
                    <Plus className="mr-1 h-3 w-3" /> Add a system
                  </Button>
                </fieldset>
                )}
                <p className="text-xs text-muted-foreground" data-testid="scanner-policy-isolation-help">
                  {ISOLATION_OPTIONS[form.readIsolation]?.help ?? ''} Oracle reads never block a writer, so this changes
                  nothing there.
                </p>
                {turningOff ? (
                  <label className="flex items-start gap-2 text-xs text-warning" data-testid="scanner-policy-confirm-off">
                    <input type="checkbox" checked={confirmOff} onChange={(e) => setConfirmOff(e.target.checked)} />
                    I understand that with the governor off a scan does not slow down or stop for a database that is
                    already struggling.
                  </label>
                ) : null}
                {formError ? (
                  <p className="text-xs text-destructive" role="alert">
                    {formError}
                  </p>
                ) : null}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled={busy || form.reason.trim().length < SCANNER_POLICY_LIMITS.reason.min}
                    onClick={() => void save()}
                  >
                    {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
                    Save policy
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
                    <X className="mr-1 h-3 w-3" /> Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {notice ? <p className="text-sm text-foreground">{notice}</p> : null}
      </div>
    </GlassCard>
  );
}
